import React, { useMemo, useState } from 'react';
import { generateLeagueTrades } from '../../utils/leagueDynamics';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

const TAGS = [
  ['buyer', 'Buyer', 'Going for it — buys win-now talent'],
  ['pat', 'Pat', 'Stands still'],
  ['seller', 'Seller', 'Rebuilding — ships players for picks'],
];
const TAG_COLOR = { buyer: '#1a9640', seller: '#c0392b', pat: '#86868B' };

function Avatar({ url, name, size = 26 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (url) {
    return <img src={url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E5E5EA' }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: '#E5E5EA', color: '#6E6E73', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, fontFamily: ff }}>
      {initial}
    </div>
  );
}

const ordPick = (n) => ['', '1st', '2nd', '3rd', '4th', '5th', '6th'][n] || `${n}th`;

// Expanded roster: players (by KTC value) + draft picks, so you can scout a team
// before tagging them a buyer or seller.
function RosterPanel({ team, distributions, vKey, nameById }) {
  const players = (team.players || [])
    .map((id) => ({ id, ...(distributions[id] || {}) }))
    .filter((p) => p.position)
    .sort((a, b) => (b[vKey] || 0) - (a[vKey] || 0));
  const picks = [...(team.picks || [])].sort(
    (a, b) => String(a.season).localeCompare(String(b.season)) || a.round - b.round
  );
  return (
    <div style={{ padding: '4px 8px 14px 44px', background: '#FAFAFA' }}>
      <div style={{ maxHeight: 232, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {players.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
            <span style={{ width: 26, fontSize: 10, fontWeight: 700, color: '#86868B', flexShrink: 0 }}>{p.position}</span>
            <span style={{ flex: 1, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name || p.id}</span>
            <span style={{ fontSize: 11, color: '#86868B', flexShrink: 0 }}>{(p[vKey] || 0) ? (p[vKey]).toLocaleString() : '—'}</span>
          </div>
        ))}
        {players.length === 0 && <div style={{ fontSize: 12, color: '#86868B', padding: '4px 0' }}>No players found.</div>}
      </div>
      {picks.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ECECEE' }}>
          <div style={{ fontSize: 9, letterSpacing: '1px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600, marginBottom: 6 }}>Draft picks</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {picks.map((pk, i) => {
              const acquired = pk.originalRosterId !== team.rosterId;
              const via = acquired ? nameById(pk.originalRosterId) : null;
              return (
                <span key={i} title={via ? `via ${via}` : 'own pick'} style={{ fontSize: 11, fontWeight: 600, color: '#1D1D1F', background: '#fff', border: acquired ? '1px solid #D2D2D7' : '1px solid #ECECEE', borderRadius: 6, padding: '3px 8px' }}>
                  {pk.season} {ordPick(pk.round)}{via ? <span style={{ color: '#86868B', fontWeight: 500 }}> · {via}</span> : ''}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Quiet = no league moves; then ~5 / ~10 / ~15 deals across the season.
const STOPS = [
  { label: 'Quiet', moves: 0 },
  { label: 'Normal', moves: 5 },
  { label: 'Active', moves: 10 },
  { label: 'Chaotic', moves: 15 },
];

export default function LeagueMoves({ bootstrap, baseResults, myRosterId, initialMoves, initialTags, initialExcluded, initialSeed, initialLocked, onApply, onClose }) {
  const teams = bootstrap.teams;
  // Your team is on the list too (pinned first), so you can opt into buying/selling.
  const ordered = useMemo(() => {
    const me = teams.find((t) => t.rosterId === myRosterId);
    const rest = teams.filter((t) => t.rosterId !== myRosterId);
    return me ? [me, ...rest] : rest;
  }, [teams, myRosterId]);

  // Start the slider near whatever was applied; default to Normal on a fresh open.
  const startIdx = (() => {
    if (!initialMoves || initialMoves <= 0) return 1; // Normal
    let best = 0, bd = Infinity;
    STOPS.forEach((s, i) => { const d = Math.abs(s.moves - initialMoves); if (d < bd) { bd = d; best = i; } });
    return best;
  })();
  const [stopIdx, setStopIdx] = useState(startIdx);
  const [tags, setTags] = useState(initialTags || {});
  const [expandedId, setExpandedId] = useState(null); // team whose roster is open
  const [excluded, setExcluded] = useState(() => new Set(initialExcluded || [])); // X'd-out trades
  const [locked, setLocked] = useState(initialLocked || []); // pinned trades that survive reroll
  // Fresh market each visit unless we're reopening an already-applied one.
  const [seed, setSeed] = useState(() => initialSeed || Math.floor(Math.random() * 1e9) || 1);

  const vKey = bootstrap.isSuperFlex ? 'ktcValueSF' : 'ktcValue1QB';
  const nameById = (rid) => teams.find((t) => t.rosterId === rid)?.displayName || `Team ${rid}`;

  const targetTrades = STOPS[stopIdx].moves;

  const { trades, summaries } = useMemo(() => generateLeagueTrades({
    teams,
    tags,
    targetTrades,
    distributions: bootstrap.playerDistributions,
    rosterPositions: bootstrap.rosterPositions,
    results: baseResults,
    isSuperFlex: bootstrap.isSuperFlex,
    currentSeason: bootstrap.season,
    pickValues: bootstrap.pickValues,
    seed,
    locked,
  }), [teams, tags, targetTrades, bootstrap, baseResults, seed, locked]);

  const byKey = {};
  summaries.forEach((s, i) => { byKey[s.key] = { trade: trades[i], summary: s }; });
  const lockedKeys = new Set(locked.map((l) => l.summary.key));

  const shown = summaries.filter((s) => !excluded.has(s.key));
  // What actually runs: everything shown (locked + unlocked) minus the X'd-out ones.
  const keptTrades = []; const keptSummaries = [];
  summaries.forEach((s, i) => { if (!excluded.has(s.key)) { keptTrades.push(trades[i]); keptSummaries.push(s); } });

  const removeTrade = (key) => {
    setExcluded((prev) => new Set(prev).add(key));
    setLocked((prev) => prev.filter((l) => l.summary.key !== key)); // X-ing also unlocks
  };
  const toggleLock = (key) => {
    setExcluded((prev) => { const n = new Set(prev); n.delete(key); return n; }); // locking un-excludes
    setLocked((prev) => prev.some((l) => l.summary.key === key)
      ? prev.filter((l) => l.summary.key !== key)
      : (byKey[key] ? [...prev, byKey[key]] : prev));
  };
  // Reroll changes only the unlocked trades; locked ones and the X'd-out set stay.
  const reroll = () => setSeed(Math.floor(Math.random() * 1e9) || 1);

  const setTag = (rosterId, tag) => setTags((prev) => ({ ...prev, [rosterId]: tag }));

  const oddsOf = (id) => Math.round((baseResults?.teams?.[id]?.playoffProbability || 0) * 100);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', fontFamily: ff }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600, marginBottom: 6 }}>League moves</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em' }}>The rest of the league trades too.</div>
          <div style={{ fontSize: 13.5, color: '#6E6E73', marginTop: 6, lineHeight: 1.5, maxWidth: 540 }}>
            Tag who's buying and who's selling, then dial in how active the league is. Sellers ship their best player to a contender for a pick — real roster changes that ripple through everyone's odds, including yours.
          </div>
        </div>
        <button onClick={onClose} style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#EBEBED', color: '#6E6E73', fontSize: 16, cursor: 'pointer' }}>×</button>
      </div>

      {/* Chaos slider */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F' }}>How active is your league?</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F' }}>{STOPS[stopIdx].label}</div>
        </div>
        <input
          type="range" min={0} max={STOPS.length - 1} step={1} value={stopIdx}
          onChange={(e) => setStopIdx(Number(e.target.value))}
          style={{ width: '100%', accentColor: '#1D1D1F', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          {STOPS.map((s, i) => (
            <button key={s.label} onClick={() => setStopIdx(i)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: i === stopIdx ? 700 : 500, color: i === stopIdx ? '#1D1D1F' : '#C7C7CC', fontFamily: ff }}>{s.label}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
        {/* Team tags */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '8px 12px' }}>
          <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600, padding: '8px 4px 6px' }}>Who's doing what</div>
          {ordered.map((t) => {
            const tag = tags[t.rosterId] || 'pat';
            const isMe = t.rosterId === myRosterId;
            const isOpen = expandedId === t.rosterId;
            return (
              <div key={t.rosterId} style={{ borderTop: '1px solid #F0F0F2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px' }}>
                  {/* Click name/avatar to scout the roster */}
                  <div
                    onClick={() => setExpandedId(isOpen ? null : t.rosterId)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}
                    title="View roster"
                  >
                    <Avatar url={t.avatarUrl} name={t.displayName} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 7 }}>
                        {t.displayName}
                        {isMe && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.5px', color: '#fff', background: '#1D1D1F', borderRadius: 5, padding: '2px 6px' }}>YOU</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#86868B' }}>{oddsOf(t.rosterId)}% playoffs · {isOpen ? 'hide roster' : 'view roster'}</div>
                    </div>
                    <span style={{ flexShrink: 0, color: '#C7C7CC', fontSize: 11, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s' }}>›</span>
                  </div>
                  <div style={{ display: 'flex', background: '#F5F5F7', borderRadius: 980, padding: 2, gap: 2, flexShrink: 0 }}>
                    {TAGS.map(([key, label]) => (
                      <button key={key} onClick={() => setTag(t.rosterId, key)} title={TAGS.find(x => x[0] === key)[2]} style={{
                        border: 'none', borderRadius: 980, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: ff,
                        background: tag === key ? (key === 'pat' ? '#1D1D1F' : TAG_COLOR[key]) : 'transparent',
                        color: tag === key ? '#fff' : '#86868B', transition: 'all 0.15s',
                      }}>{label}</button>
                    ))}
                  </div>
                </div>
                {isOpen && <RosterPanel team={t} distributions={bootstrap.playerDistributions} vKey={vKey} nameById={nameById} />}
              </div>
            );
          })}
        </div>

        {/* Generated trades preview */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', fontWeight: 600 }}>
              {targetTrades === 0
                ? 'Quiet — nobody else trades'
                : shown.length
                  ? `${shown.length} trade${shown.length === 1 ? '' : 's'} this season${excluded.size ? ' · X out any you don’t buy' : ''}`
                  : 'Tag some buyers and sellers'}
            </div>
            {targetTrades > 0 && (
              <button onClick={reroll} title="Generate a different set of trades" style={{ flexShrink: 0, border: 'none', background: '#F5F5F7', borderRadius: 980, padding: '4px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#1D1D1F', fontFamily: ff }}>↻ Shuffle</button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {shown.map((s) => {
              const isLocked = lockedKeys.has(s.key);
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#1D1D1F', lineHeight: 1.45, padding: '4px 6px', borderRadius: 8, background: isLocked ? '#F5F5F7' : 'transparent' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700 }}>{s.buyerName}</span>
                    <span style={{ color: '#86868B' }}> gets </span>
                    <span style={{ fontWeight: 700 }}>{s.getPlayer}</span>
                    <span style={{ color: '#86868B' }}> from {s.sellerName} for </span>
                    <span>{s.returnLabels.join(', ')}</span>
                  </div>
                  <button
                    onClick={() => toggleLock(s.key)}
                    title={isLocked ? 'Unlock — let reroll change it' : 'Lock this trade — reroll keeps it'}
                    style={{ flexShrink: 0, border: 'none', borderRadius: 980, padding: '3px 9px', background: isLocked ? '#1D1D1F' : '#EBEBED', color: isLocked ? '#fff' : '#86868B', fontSize: 10, fontWeight: 700, letterSpacing: '0.3px', cursor: 'pointer', fontFamily: ff, marginTop: 1 }}
                  >{isLocked ? 'Locked' : 'Lock'}</button>
                  <button
                    onClick={() => removeTrade(s.key)}
                    title="Remove this trade"
                    style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#EBEBED', color: '#86868B', fontSize: 12, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, marginTop: 1 }}
                  >×</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ border: 'none', background: 'none', color: '#6E6E73', fontSize: 14, fontWeight: 500, padding: '12px 18px', cursor: 'pointer', fontFamily: ff }}>Cancel</button>
        <button onClick={() => onApply({ moves: targetTrades, tags, seed, excluded: [...excluded], locked, trades: keptTrades, summaries: keptSummaries })} style={{
          border: 'none', borderRadius: 980, padding: '13px 30px', cursor: 'pointer',
          background: '#000', color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: ff, letterSpacing: '-0.01em',
        }}>{targetTrades === 0 ? 'Run quiet league' : 'Run the league'}</button>
      </div>
    </div>
  );
}
